import { addDoc, doc, getDocs, query, updateDoc } from 'firebase/firestore';
import { Reward, rewardRef } from '@api/Rewards/Reward';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeChanges } from '@api/ActivityLogs/DescribeChanges';
import { REWARD_FIELDS, flattenReward } from '@api/ActivityLogs/CustomerActivity';

/** Id fijo de la bitácora del programa de puntos. Ver `saveReward`. */
export const REWARD_ACTIVITY_ID = 'programa-de-puntos';

export default {
    /**
     * Guarda la tasa de puntos. **Es un singleton que gobierna cuánto gana la
     * cadena entera en cada compra**, y hasta el 2026-09-11 se podía mover sin
     * dejar ni un rastro (M4 de la bitácora).
     *
     * ⚠️ El renglón va bajo un **id FIJO** (`REWARD_ACTIVITY_ID`) y no bajo el id
     * del documento: `reward` es una colección con un solo doc, y su id ha
     * cambiado —`saveReward` crea uno nuevo si llega sin id—. Con el id real, el
     * historial se partiría en dos el día que eso pase, que es justo el día en que
     * alguien querría leerlo completo.
     *
     * Costo: 1 lectura del estado anterior + 1 escritura, y **solo si cambió
     * algo**. Se guarda unas cuantas veces al año.
     */
    async saveReward(reward: Reward): Promise<Reward> {
        // Solo para la bitácora: un fallo leyendo el estado anterior no puede
        // impedir que se guarde el programa de puntos.
        let anterior: Reward | undefined;
        try {
            anterior = await this.getRewards();
        } catch (error) {
            console.error('No se pudo leer el programa anterior para la bitácora', error);
        }
        if (!reward.id) {
            const data = await addDoc(rewardRef, reward);
            reward.id = data.id;
        }
        await updateDoc(doc(rewardRef, reward.id), { ...reward });
        try {
            const frase = describeChanges(flattenReward(anterior), flattenReward(reward), REWARD_FIELDS);
            if (frase) {
                await ActivityLogServ.addActivityLog('reward', REWARD_ACTIVITY_ID, frase);
            }
        } catch (error) {
            console.error('No se pudo registrar el cambio del programa de puntos', error);
        }
        return reward;
    },
    async getRewards(): Promise<Reward | undefined> {
        let reward = undefined;
        const queryDoc = query(rewardRef);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return reward;
        }
        snapshot.docs.map((doc: any) => {
            reward = doc.data() as Reward;
        });
        return reward;
    }
};
